import { Header } from '../components/Header';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { useAuth } from '../contexts/AuthContext';

const heatRegions = [
  { name: 'Centro', city: 'Brasília', intensity: 92, sentiment: 71, voters: 18400, trend: '+14%' },
  { name: 'Taguatinga', city: 'Brasília', intensity: 78, sentiment: 64, voters: 13200, trend: '+9%' },
  { name: 'Ceilândia', city: 'Brasília', intensity: 86, sentiment: 58, voters: 22100, trend: '+5%' },
  { name: 'Samambaia', city: 'Brasília', intensity: 61, sentiment: 49, voters: 9800, trend: '-2%' },
  { name: 'Plano Piloto', city: 'Brasília', intensity: 74, sentiment: 67, voters: 7600, trend: '+7%' },
  { name: 'Gama', city: 'Brasília', intensity: 53, sentiment: 44, voters: 6900, trend: '-4%' },
  { name: 'Sobradinho', city: 'Brasília', intensity: 48, sentiment: 52, voters: 5100, trend: '+1%' },
  { name: 'Santa Maria', city: 'Brasília', intensity: 69, sentiment: 55, voters: 8300, trend: '+6%' },
];

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

function heatClass(intensity: number) {
  if (intensity >= 85) return 'bg-accent text-white shadow-glow';
  if (intensity >= 70) return 'bg-primary text-primary-foreground shadow-soft';
  if (intensity >= 55) return 'bg-highlight text-highlight-foreground';
  return 'bg-muted text-muted-foreground';
}

export function AnalyticsPage() {
  const { user } = useAuth();
  const strongestRegion = heatRegions.reduce((current, region) =>
    region.intensity > current.intensity ? region : current
  );

  return (
    <>
      <Header
        title="Analytics"
        subtitle="Indicadores de performance, crescimento e exportações da operação."
      />
      <div className="space-y-6 p-6">
        <section className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Mapa de calor</p>
              <h2 className="mt-2 text-2xl font-semibold text-foreground">Engajamento por região</h2>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                Visualização mockada para acompanhar concentração de eleitores, sentimento favorável e tendência regional da campanha.
              </p>
            </div>
            <div className="rounded-2xl bg-accent/10 px-4 py-3 text-sm font-semibold text-accent">
              Região mais quente: {strongestRegion.name}
            </div>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-3xl border border-border bg-muted/40 p-5">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {heatRegions.map((region) => (
                  <button
                    key={region.name}
                    className={`min-h-28 rounded-2xl p-4 text-left transition-transform duration-200 hover:-translate-y-0.5 ${heatClass(region.intensity)}`}
                  >
                    <p className="text-sm font-semibold">{region.name}</p>
                    <p className="mt-1 text-xs opacity-75">{region.city}</p>
                    <p className="mt-4 text-2xl font-bold">{region.intensity}</p>
                    <p className="text-xs opacity-75">índice de calor</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Ranking regional</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Intensidade, sentimento e base estimada.</p>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {[...heatRegions]
                  .sort((a, b) => b.intensity - a.intensity)
                  .map((region) => (
                    <div key={region.name} className="rounded-2xl border border-border bg-muted/30 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-foreground">{region.name}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatNumber(region.voters)} eleitores estimados | sentimento {region.sentiment}%
                          </p>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${region.trend.startsWith('-') ? 'bg-red-50 text-red-600' : 'bg-success/10 text-success'}`}>
                          {region.trend}
                        </span>
                      </div>
                      <div className="mt-4 h-2 overflow-hidden rounded-full bg-border">
                        <div className="h-full rounded-full bg-gradient-primary" style={{ width: `${region.intensity}%` }} />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>

        <AnalyticsDashboard isSystemView={user?.role === 'SUPERADMIN'} />
      </div>
    </>
  );
}
